import { type ReactNode, useId } from 'react';
import { TopDialog } from './Dialog';

/**
 * Centered dialog for blocking choices only (most things belong in a Sheet). Top layer, Escape
 * and backdrop close it unless `dismissible` is false.
 *
 *   <Modal open={open} onClose={close} title="Keeper change"
 *     actions={<><Button onClick={close}>Nah</Button><Button variant="primary">Take it</Button></>}>
 *     Giorgos has the book. Take it?
 *   </Modal>
 */
export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
  dismissible?: boolean;
}

export function Modal(props: ModalProps) {
  if (!props.open) return null;
  return <ModalBody {...props} />;
}

function ModalBody({ onClose, title, children, actions, dismissible = true }: ModalProps) {
  const id = useId();
  return (
    <TopDialog
      onClose={onClose}
      dismissible={dismissible}
      labelledBy={`${id}-t`}
      describedBy={children ? `${id}-b` : undefined}
    >
      <div className="pointer-events-none grid h-full place-items-center px-4">
        <div className="tts-ui-modal-panel tts-card pointer-events-auto flex w-full max-w-sm flex-col gap-4 p-5">
          <h2 id={`${id}-t`} className="tts-display text-2xl">
            {title}
          </h2>
          {children && (
            <div id={`${id}-b`} className="text-base">
              {children}
            </div>
          )}
          {actions && <div className="flex flex-wrap justify-end gap-3 pt-1">{actions}</div>}
        </div>
      </div>
    </TopDialog>
  );
}
